import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import FAQStructuredData, { extractFAQFromContent } from '../components/FAQStructuredData';
import {
  Calendar,
  Clock,
  Tag,
  ArrowLeft,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Check,
  MessageCircle,
  Send
} from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import {
  shareToFacebook,
  shareToTwitter,
  shareToLinkedIn,
  copyToClipboard,
  shareToWhatsApp,
  shareToTelegram,
  getCurrentUrl,
  ShareData
} from '../utils/shareUtils';
import { useSEO } from '../hooks/useSEO';
import { ArticleStructuredData } from '../components/StructuredData';
import RatingStructuredData from '../components/RatingStructuredData';
import { processContent } from '../utils/contentProcessor';
import JurosCompostos from '../components/calculators/JurosCompostos';
import Financiamento from '../components/calculators/Financiamento';
import CartaoCredito from '../components/calculators/CartaoCredito';
import Economia from '../components/calculators/Economia';
import CarroAssinatura from '../components/calculators/CarroAssinatura';
import ComparadorRendaFixa from '../components/calculators/ComparadorRendaFixa';
import ContaGlobal from '../components/calculators/ContaGlobal';
import StickyShareBar from '../components/StickyShareBar';
import NewsletterSignup from '../components/NewsletterSignup';
// New engagement components
import FeedbackWidget from '../components/FeedbackWidget';
import QuickSummary from '../components/QuickSummary';
import LastUpdatedBadge from '../components/LastUpdatedBadge';
import FloatingCTA from '../components/FloatingCTA';
import KeyTakeaways from '../components/KeyTakeaways';
import ComparisonFilter from '../components/ComparisonFilter';
import RecommendationQuiz from '../components/RecommendationQuiz';

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  const post = blogPosts.find(p => p.id === id);

  const { processedContent, headings } = React.useMemo(() => {
    if (!post) return { processedContent: '', headings: [] };
    return processContent(post.content);
  }, [post]);

  // Smart related posts algorithm
  const relatedPosts = React.useMemo(() => {
    if (!post) return [];

    return blogPosts
      .filter(p => p.id !== post.id)
      .map(p => {
        let score = 0;
        
        // 1. Match by tags (highest weight)
        const sharedTags = p.tags.filter(tag => post.tags.includes(tag));
        score += sharedTags.length * 5;

        // 2. Match by category (medium weight)
        if (p.category === post.category) score += 3;

        // 3. Match by year/recency (low weight)
        const pYear = new Date(p.date).getFullYear();
        const postYear = new Date(post.date).getFullYear();
        if (pYear === postYear) score += 2;

        // 4. Boost featured posts slightly
        if (p.featured) score += 1;

        return { ...p, score };
      })
      .filter(p => p.score > 0) // Only show actually related posts
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Tie-breaker: most recent first
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      })
      .slice(0, 3);
  }, [post]);

  const [readingProgress, setReadingProgress] = useState(0);

  // Reading progress effect
  useEffect(() => {
    const updateProgress = () => {
      const currentProgress = window.scrollY;
      const scrollHeight = document.body.scrollHeight - window.innerHeight;
      if (scrollHeight) {
        setReadingProgress(Number((currentProgress / scrollHeight).toFixed(2)) * 100);
      }
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // SEO implementation - must be called before any early returns
  useSEO(post ? {
    title: `${post.title} | O Teu Dinheiro`,
    description: post.excerpt,
    keywords: post.tags.join(', '),
    url: getCurrentUrl(),
    type: 'article',
    author: 'O Teu Dinheiro',
    publishedTime: new Date(post.date).toISOString(),
    modifiedTime: post.lastUpdated ? new Date(post.lastUpdated).toISOString() : new Date(post.date).toISOString(),
    section: post.category,
    tags: post.tags
  } : {
    title: 'Artigo não encontrado | O Teu Dinheiro',
    description: 'O artigo que você está procurando não existe.',
    type: 'website'
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Artigo não encontrado</h1>
          <p className="text-gray-600 mb-8">O artigo que você está procurando não existe.</p>
          <Link to="/blog" className="btn btn-primary">
            Voltar ao Blog
          </Link>
        </div>
      </div>
    );
  }



  // Share functions
  const currentUrl = getCurrentUrl() || (typeof window !== 'undefined' ? window.location.href : '');
  const shareData: ShareData = {
    title: post.title,
    url: currentUrl,
    description: post.excerpt,
    hashtags: post.tags
  };

  const handleShare = (platform: string) => {
    switch (platform) {
      case 'facebook':
        shareToFacebook(shareData);
        break;
      case 'twitter':
        shareToTwitter(shareData);
        break;
      case 'linkedin':
        shareToLinkedIn(shareData);
        break;
      case 'whatsapp':
        shareToWhatsApp(shareData);
        break;
      case 'telegram':
        shareToTelegram(shareData);
        break;
      case 'copy':
        handleCopyLink();
        break;
    }
    setShowShareMenu(false);
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(currentUrl);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };



  return (
    <div className="min-h-screen bg-gray-50">
      <StickyShareBar title={post.title} url={window.location.href} />
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50 bg-gray-200">
        <div
          className="h-full bg-primary-600 transition-all duration-150 ease-out"
          style={{ width: `${readingProgress}%` }}
        />
      </div>
      {/* Structured Data */}
      <ArticleStructuredData
        title={post.title}
        description={post.excerpt}
        url={currentUrl}
        author="O Teu Dinheiro"
        publishedTime={new Date(post.date).toISOString()}
        modifiedTime={post.lastUpdated ? new Date(post.lastUpdated).toISOString() : new Date(post.date).toISOString()}
        section={post.category}
        tags={post.tags}
      />
      {/* FAQ Structured Data - auto-extracted from content */}
      <FAQStructuredData items={extractFAQFromContent(post.content)} />
      {/* Rating Structured Data for comparison/reviews */}
      {post.rating && (
        <RatingStructuredData
          name={post.title}
          description={post.excerpt}
          ratingValue={post.rating.value}
          reviewCount={post.rating.count}
          url={currentUrl}
          image={post.image}
        />
      )}
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container-custom py-6">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Blog
          </Link>

          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
            <Tag className="w-4 h-4" />
            <span className="bg-primary-100 text-primary-600 px-2 py-1 rounded-full text-xs">
              {post.category}
            </span>
            {post.featured && (
              <span className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs">
                Destaque
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {new Date(post.date).toLocaleDateString('pt-BR')}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {post.readTime}
              </span>
              {post.lastUpdated && (
                <LastUpdatedBadge date={post.lastUpdated} />
              )}
            </div>

            <div className="flex items-center space-x-2">
              <div className="relative" ref={shareMenuRef}>
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Compartilhar"
                >
                  <Share2 className="w-5 h-5" />
                </button>

                {/* Share Menu Dropdown */}
                {showShareMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Facebook className="w-4 h-4 text-blue-600" />
                      <span>Facebook</span>
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Twitter className="w-4 h-4 text-blue-400" />
                      <span>Twitter</span>
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Linkedin className="w-4 h-4 text-blue-700" />
                      <span>LinkedIn</span>
                    </button>
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <MessageCircle className="w-4 h-4 text-green-600" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      onClick={() => handleShare('telegram')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Send className="w-4 h-4 text-blue-500" />
                      <span>Telegram</span>
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                    >
                      {copySuccess ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600" />
                      )}
                      <span>{copySuccess ? 'Copiado!' : 'Copiar Link'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-xl shadow-lg p-8">
              {/* Excerpt */}
              <div className="bg-primary-50 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
                <p className="text-lg text-gray-700 italic">
                  {post.excerpt}
                </p>
              </div>

              {/* Quick Summary (TL;DR) */}
              {post.quickSummary && post.quickSummary.length > 0 && (
                <QuickSummary points={post.quickSummary} />
              )}

              {/* Primary Above-the-Fold Calculator for Calculator/Comparison Slugs */}
              {post.id === 'carro-assinatura-calculadora' && (
                <div className="my-8 bg-indigo-50/40 p-6 rounded-xl shadow-md border-2 border-indigo-200">
                  <CarroAssinatura />
                </div>
              )}

              {post.id === 'nomad-wise-global-2026' && (
                <div className="my-8 bg-indigo-50/40 p-6 rounded-xl shadow-md border-2 border-indigo-200">
                  <ContaGlobal />
                </div>
              )}

              {post.id === 'fim-isencao-imposto-renda-lci-lca-2026' && (
                <div className="my-8 bg-indigo-50/40 p-6 rounded-xl shadow-md border-2 border-indigo-200">
                  <ComparadorRendaFixa />
                </div>
              )}

              {/* Recommendation Quiz for comparison posts */}
              {post.quizQuestions && post.quizResults && (
                <RecommendationQuiz
                  questions={post.quizQuestions}
                  results={post.quizResults}
                />
              )}

              {/* Comparison Filter */}
              {post.isComparison && post.filterOptions && (
                <div>
                  <ComparisonFilter
                    options={post.filterOptions}
                    onFilterChange={(filters) => setActiveFilters(filters)}
                  />
                  {activeFilters.length > 0 && (
                    <div className="mb-8 p-5 bg-indigo-50 border border-indigo-200 rounded-xl text-sm text-indigo-900">
                      <p className="font-bold mb-2">🎯 Recomendação rápida para os filtros selecionados:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {activeFilters.includes('milhas') && (
                          <li><strong>Milhas/Pontos:</strong> BRB Dux Visa Infinite (5,0 a 7,0 pts/US$) e C6 Graphene (4,0 pts/US$ que não expiram).</li>
                        )}
                        {activeFilters.includes('cashback') && (
                          <li><strong>Cashback:</strong> XP Legacy (1,5% Investback) e Nubank Ultravioleta (1% rendendo 200% do CDI).</li>
                        )}
                        {activeFilters.includes('salavip') && (
                          <li><strong>Sala VIP Ilimitada:</strong> BRB Dux, C6 Graphene e Santander Unlimited (LoungeKey / DragonPass ilimitados).</li>
                        )}
                        {activeFilters.includes('semanuidade') && (
                          <li><strong>Isenção mais acessível:</strong> Banco Inter Black (via Duo Gourmet ou R$ 250 mil investidos) e XP Visa Infinite.</li>
                        )}
                        {activeFilters.includes('internacional') && (
                          <li><strong>Viagem Internacional:</strong> C6 Graphene (chip roaming global incluso) ou combinar seu Black com conta global Wise/Nomad (IOF 1,1%).</li>
                        )}
                        {activeFilters.includes('imobiliario') && (
                          <li><strong>Tokenização Imobiliária:</strong> Foque em plataformas reguladas pela CVM e FIIs de tijolo com dividendos isentos.</li>
                        )}
                        {activeFilters.includes('agro') && (
                          <li><strong>Agro / Fiagros:</strong> Excelente prêmio de risco atrelado ao CDI+, ideal para diversificação de renda mensal.</li>
                        )}
                        {activeFilters.includes('judicial') && (
                          <li><strong>Precatórios / Judicial:</strong> Alta rentabilidade prefixada ou IPCA+, recomendado para horizonte de 24 a 48 meses.</li>
                        )}
                        {activeFilters.includes('rentabilidade') && (
                          <li><strong>Maior Rentabilidade:</strong> Priorize ativos IPCA+ de longo prazo ou ETFs cripto/ações para multiplicação de capital.</li>
                        )}
                        {activeFilters.includes('seguranca') && (
                          <li><strong>Maior Segurança:</strong> Priorize Tesouro Selic e LCIs/LCAs/CDBs dentro do limite de R$ 250 mil do FGC.</li>
                        )}
                        {activeFilters.includes('liquidez') && (
                          <li><strong>Liquidez Diária:</strong> Tesouro Selic, CDB 100%+ CDI com resgate D+0 ou fundos DI taxa zero.</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Key Takeaways */}
              {post.keyTakeaways && post.keyTakeaways.length > 0 && (
                <KeyTakeaways items={post.keyTakeaways} />
              )}

              {/* Content */}
              <div className="prose prose-lg max-w-none mb-12 text-gray-800"
                dangerouslySetInnerHTML={{ __html: processedContent }} />

              {/* Embedded Calculators for Other Posts Matching Tags */}
              {post.id !== 'carro-assinatura-calculadora' && post.tags.some(tag => tag.toLowerCase().includes('assinatura') || tag.toLowerCase().includes('carro')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <CarroAssinatura />
                </div>
              )}

              {post.id !== 'fim-isencao-imposto-renda-lci-lca-2026' && post.tags.some(tag => tag.toLowerCase() === 'lci' || tag.toLowerCase() === 'lca' || tag.toLowerCase().includes('renda fixa')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <ComparadorRendaFixa />
                </div>
              )}

              {post.id !== 'nomad-wise-global-2026' && post.tags.some(tag => tag.toLowerCase().includes('conta global') || tag.toLowerCase().includes('câmbio') || tag.toLowerCase().includes('dólar')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <ContaGlobal />
                </div>
              )}

              {post.tags.some(tag => tag.toLowerCase().includes('juros') || tag.toLowerCase().includes('investimento')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <h3 className="text-xl font-bold text-indigo-900 mb-4">Simule seus Investimentos</h3>
                  <JurosCompostos />
                </div>
              )}

              {post.tags.some(tag => tag.toLowerCase().includes('imóvel') || tag.toLowerCase().includes('casa') || tag.toLowerCase().includes('financiamento')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <h3 className="text-xl font-bold text-indigo-900 mb-4">Simule seu Financiamento</h3>
                  <Financiamento />
                </div>
              )}

              {post.tags.some(tag => tag.toLowerCase().includes('dívida') || tag.toLowerCase().includes('cartão')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <h3 className="text-xl font-bold text-indigo-900 mb-4">Calcule os Juros do Cartão</h3>
                  <CartaoCredito />
                </div>
              )}

              {post.tags.some(tag => tag.toLowerCase().includes('economia') || tag.toLowerCase().includes('meta')) && (
                <div className="my-12 bg-white p-6 rounded-xl shadow-md border border-indigo-100">
                  <h3 className="text-xl font-bold text-indigo-900 mb-4">Planeje sua Meta</h3>
                  <Economia />
                </div>
              )}

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog?tag=${encodeURIComponent(tag)}`}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compartilhar:</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                  <button
                    onClick={() => handleShare('telegram')}
                    className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    <span>Telegram</span>
                  </button>
                  <button
                    onClick={() => handleShare('copy')}
                    className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    {copySuccess ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    <span>{copySuccess ? 'Copiado!' : 'Copiar Link'}</span>
                  </button>
                </div>
              </div>
              {/* Author Bio */}
              {/*
              <AuthorBio authorName={post.author} />
              */}

              {/* Feedback Widget */}
              <FeedbackWidget postId={post.id} />

              {/* Related Posts - Inline (mobile-friendly) */}
              {relatedPosts.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📖 Leia Também</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.id}`}
                        className="block bg-gray-50 hover:bg-primary-50 p-4 rounded-xl transition-colors border border-gray-100 hover:border-primary-200"
                      >
                        <span className="inline-block bg-primary-100 text-primary-600 px-2 py-0.5 rounded-full text-xs mb-2">
                          {relatedPost.category}
                        </span>
                        <h4 className="font-medium text-gray-900 mb-1 line-clamp-2 text-sm">
                          {relatedPost.title}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {new Date(relatedPost.date).toLocaleDateString('pt-BR')}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Signup */}
              <NewsletterSignup />

              {/* CTA Section */}
              <div className="my-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Quer organizar suas finanças de verdade?
                </h3>
                <p className="text-indigo-100 mb-6">
                  Utilize nossas calculadoras e planilhas gratuitas para assumir o controle do seu dinheiro hoje mesmo.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/calculadoras"
                    className="btn btn-primary"
                  >
                    Acessar Calculadoras
                  </Link>
                  <Link
                    to="/planilhas"
                    className="btn btn-secondary bg-white text-primary-600 hover:bg-gray-50"
                  >
                    Baixar Planilhas
                  </Link>
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-8">

              {/* Table of Contents */}
              {headings.length > 0 && (
                <div className="card p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Neste Artigo
                  </h3>
                  <nav className="space-y-2">
                    {headings.map((heading) => (
                      <a
                        key={heading.id}
                        href={`#${heading.id}`}
                        className={`block text-sm hover:text-primary-600 transition-colors ${heading.level === 3 ? 'pl-4 text-gray-500' : 'text-gray-700 font-medium'
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        {heading.text}
                      </a>
                    ))}
                  </nav>
                </div>
              )}

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="card p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Artigos Relacionados
                  </h3>
                  <div className="space-y-4">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.id}`}
                        className="block hover:bg-gray-50 p-3 rounded-lg transition-colors"
                      >
                        <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">
                          {relatedPost.title}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {new Date(relatedPost.date).toLocaleDateString('pt-BR')}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating CTA */}
      <FloatingCTA />
    </div>
  );
};

export default BlogPost;
